import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { styled } from 'dripsy';
import { useNavigation, useRoute } from '@react-navigation/native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';

import { useNavOptionsSet } from '@hooks';
import { IS_WEB, parseApiError } from '@utils';
import { BaseText, TrackFeedbackForm } from '@components';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { TrackFeedbackFormDataType } from '@validation';
import { AnalyticsService, ToastService } from '@services';
import { FeedbackNavigatorRoutePropType } from '@navigation';

const Container = styled(KeyboardAwareScrollView)({
  flex: 1,
  bg: '$background0',
});

const ContentContainer = styled(View)({
  flex: 1,
  mx: '$5',
});

const Title = styled(BaseText)({
  color: '$heading800',
  variant: 'text.xbig',
  fontWeight: 'semi',
});

type RoutePropType =
  FeedbackNavigatorRoutePropType<'TrackFeedbackForm'>;

export const TrackFeedbackFormScreen = observer(() => {
  const { t } = useTranslation();
  const trackModel = useTracksModel();
  const route = useRoute<RoutePropType>();

  const { goBack } = useNavigation();

  useNavOptionsSet({
    title: t('common.trackFeedbackForm.title'),
  });

  async function submitFeedback(
    formValues: TrackFeedbackFormDataType,
  ) {
    try {
      await trackModel.feedback.sendTrackFeedback.run({
        feedbackOptionType: route.params?.feedbackOptionType,
        ...formValues,
      });

      ToastService.showSuccess(t('trackFeedback.successMessage'));
      AnalyticsService.trackSubmitFeedback();
      goBack();
    } catch (error) {
      ToastService.showError(parseApiError(error).message);
    }
  }

  return (
    <Container>
      <ContentContainer>
        {IS_WEB && (
          <Title>{t('common.trackFeedbackForm.title')}</Title>
        )}
        <TrackFeedbackForm
          isFormSubmitting={
            trackModel.feedback.sendTrackFeedback.inProgress
          }
          onCancel={goBack}
          onSubmit={submitFeedback}
        />
      </ContentContainer>
    </Container>
  );
});
